import { useState, useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import { api, postWaitlist, BASE_URL } from '../lib/api'

export function LandingPage() {
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false)
  const beliefDetailsRef = useRef<HTMLDetailsElement>(null)

  // Self-Demo / Try Flow State
  const [tryStep, setTryStep] = useState<'input' | 'nudge' | 'email'>('input')
  const [userName, setUserName] = useState('')
  const [userRole, setUserRole] = useState('manager')
  const [userFocus, setUserFocus] = useState('morale')
  const [demoNudge, setDemoNudge] = useState<any>(null)
  
  // Enterprise waitlist state (demoted)
  const [finalStep, setFinalStep] = useState<'email' | 'challenge' | 'success'>('email')
  const [finalEmail, setFinalEmail] = useState('')
  const [finalChallenge, setFinalChallenge] = useState('')
  const [finalWaitlistLoading, setFinalWaitlistLoading] = useState(false)
  const [finalWaitlistError, setFinalWaitlistError] = useState<string | null>(null)

  const FOCUS_OPTIONS = [
    { id: 'feedback', label: 'Giving feedback to an underperformer' },
    { id: 'onboarding', label: 'Onboarding a new hire' },
    { id: 'morale', label: 'Team morale feels low' },
    { id: 'leadership', label: 'Improving my own leadership habits' },
  ]

  const DEMO_NUDGES: Record<string, any> = {
    feedback: {
      category: 'leadership',
      title: 'Ask, Don\'t Tell',
      body: 'In your next talk with them, replace one instruction with a question: "What do you think the first step should be?" Inquiry builds stronger problem-solvers than directives.',
      science: 'Edmondson, Harvard Business Review, 2019'
    },
    onboarding: {
      category: 'onboarding',
      title: 'The First 90 Check-In',
      body: 'Send a quick message: "I want to make sure I\'m supporting you well. What should I keep doing? Stop doing? Start doing?" Early feedback loops predict success 12 months out.',
      science: 'Watkins, The First 90 Days, 2013'
    },
    morale: {
      category: 'recognition',
      title: 'The 24-Hour Thank You',
      body: 'Identify one person who did something small but helpful today. Thank them before you log off. Gratitude expressed quickly predicts lower team turnover.',
      science: 'Grant & Gino, JPSP, 2010'
    },
    leadership: {
      category: 'leadership',
      title: 'Ask for Bad News First',
      body: 'In your next team meeting, open with: "What\'s going worse than we hoped?" Leaders who surface bad news early detect problems 3x faster.',
      science: 'Detert & Edmondson, AMJ, 2011'
    }
  }

  const [demoEmail, setDemoEmail] = useState('')
  const [isSendingDemo, setIsSendingDemo] = useState(false)
  const [demoSent, setDemoSent] = useState(false)

  const handleSendEmail = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSendingDemo(true)
    try {
      const res = await fetch(`${BASE_URL}/api/demo/send-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: demoEmail,
          name: userName,
          nudge: demoNudge
        })
      })
      if (res.ok) {
        setDemoSent(true)
        setTryStep('input') // Reset or show success
        alert("🕊 Done! Check your email. We've sent you this nudge and a follow-up will arrive in 48 hours.")
      }
    } catch (err) {
      console.error(err)
    } finally {
      setIsSendingDemo(false)
    }
  }

  const handleStartDemo = (e: React.FormEvent) => {
    e.preventDefault()
    setDemoNudge(DEMO_NUDGES[userFocus])
    setTryStep('nudge')
  }

  const [isMobile, setIsMobile] = useState(window.innerWidth < 900)
  const [founderVideoId, setFounderVideoId] = useState('4c6aa6e3fe9c4bec84ba6b8eb991b75d')

  function openBeliefScience(e: React.MouseEvent<HTMLAnchorElement>) {
    e.preventDefault()
    const el = beliefDetailsRef.current
    if (el) {
      el.open = true
      el.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
    }
  }

  const handleFinalEmail = (e: React.FormEvent) => {
    e.preventDefault()
    if (!finalEmail || !finalEmail.includes('@')) return
    setFinalStep('challenge')
  }

  async function handleFinalChallenge(challenge: string) {
    setFinalChallenge(challenge)
    setFinalWaitlistLoading(true)
    setFinalWaitlistError(null)
    try {
      await postWaitlist(finalEmail, challenge)
      setFinalStep('success')
    } catch (e: unknown) {
      setFinalWaitlistError(e instanceof Error ? e.message : 'Something went wrong. Please try again.')
    } finally {
      setFinalWaitlistLoading(false)
    }
  }

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 900)
    window.addEventListener('resize', handleResize)

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          (entry.target as HTMLElement).style.animation = 'fade-up 0.6s ease both'
          observer.unobserve(entry.target)
        }
      })
    }, { threshold: 0.1 })

    document.querySelectorAll('.step-card, .usecase-card, .testimonial-card, .showcase-nudge-card, .founder-pillar, .founder-video-container, .audience-card').forEach(el => {
      (el as HTMLElement).style.opacity = '0'
      observer.observe(el)
    })

    return () => {
      observer.disconnect()
      window.removeEventListener('resize', handleResize)
    }
  }, [])

  useEffect(() => {
    setFounderVideoId(isMobile ? 'dd8bf9f3c2e9454e926ba5b7153740ac' : '4c6aa6e3fe9c4bec84ba6b8eb991b75d')
  }, [isMobile])

  return (
    <div className="landing-root">
      <style>{`
        :root {
          --cream:      #FDFCF8;
          --cream-dark: #F5F2EA;
          --sky:        #C8E6F0;
          --sky-light:  #EBF6FB;
          --sky-dim:    rgba(200,230,240,0.35);
          --sage:       #C4D4BE;
          --sage-light: #E8F0E5;
          --teal:       #2A7A8A;
          --teal-deep:  #1A5F6E;
          --teal-pale:  rgba(42,122,138,0.10);
          --teal-pale2: rgba(42,122,138,0.06);
          --warm-text:  #2C3E35;
          --muted-text: #4a5750;
          --faint-text: #5f7169;
          --white:      #FFFFFF;
          --dark-teal:  #1A3C40;
          --dark-teal2: #112829;
          --glow-amber: rgba(251,191,36,0.10);
          --amber:      #D4921A;
          --amber-pale: rgba(212,146,26,0.12);
        }

        .landing-root {
          font-family: 'DM Sans', system-ui, sans-serif;
          background: var(--cream);
          color: var(--warm-text);
          -webkit-font-smoothing: antialiased;
          overflow-x: hidden;
          font-size: 16px;
          line-height: 1.6;
        }

        @keyframes breathe {
          0%, 100% { transform: scale(1); opacity: 0.7; }
          50% { transform: scale(1.06); opacity: 1; }
        }

        @keyframes float {
          0%, 100% { transform: translateY(0px) rotate(-1.5deg); }
          50% { transform: translateY(-14px) rotate(-1.5deg); }
        }

        @keyframes float2 {
          0%, 100% { transform: translateY(0px) rotate(1deg); }
          50% { transform: translateY(-10px) rotate(1deg); }
        }

        @keyframes float3 {
          0%, 100% { transform: translateY(0px) rotate(-0.5deg); }
          50% { transform: translateY(-8px) rotate(-0.5deg); }
        }

        @keyframes glow-pulse {
          0%, 100% { opacity: 0.25; }
          50% { opacity: 0.55; }
        }

        @keyframes fade-up {
          from { opacity: 0; transform: translateY(24px); }
          to { opacity: 1; transform: translateY(0); }
        }

        @keyframes fade-in {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        nav {
          position: sticky; top: 0; left: 0; right: 0; z-index: 100;
          display: flex; align-items: center; justify-content: space-between;
          padding: 0 48px;
          height: 68px;
          background: rgba(253,252,248,0.92);
          backdrop-filter: blur(16px);
          border-bottom: 1px solid rgba(196,212,190,0.4);
        }

        .nav-logo {
          display: flex; align-items: center; gap: 10px;
          text-decoration: none;
          font-family: 'Playfair Display', serif;
          font-size: 1.35rem; font-weight: 600;
          color: var(--teal-deep);
          letter-spacing: -0.3px;
        }

        .nav-logo-mark {
          width: 36px; height: 36px; border-radius: 50%;
          background: var(--sky-light);
          border: 1.5px solid var(--sky);
          display: flex; align-items: center; justify-content: center;
          font-size: 18px; line-height: 1;
        }

        .nav-links {
          display: flex; align-items: center; gap: 36px;
        }

        .nav-links a {
          font-size: 0.875rem; font-weight: 400;
          color: var(--muted-text); text-decoration: none;
          transition: color 0.15s;
        }
        .nav-links a:hover { color: var(--warm-text); }

        .nav-actions { display: flex; align-items: center; gap: 12px; }

        .nav-signin {
          font-size: 0.875rem; font-weight: 400;
          color: var(--muted-text); text-decoration: none;
          transition: color 0.15s;
        }
        .nav-signin:hover { color: var(--warm-text); }

        .nav-cta {
          background: var(--teal);
          color: white; text-decoration: none;
          padding: 9px 22px;
          border-radius: 100px;
          font-size: 0.875rem; font-weight: 500;
          transition: background 0.15s, transform 0.1s;
          white-space: nowrap;
        }
        .nav-cta:hover { background: var(--teal-deep); transform: translateY(-1px); }

        .nav-burger {
          display: none;
          background: none; border: none; cursor: pointer;
          padding: 8px; color: var(--warm-text);
          flex-direction: column; gap: 5px;
        }
        .nav-burger span {
          display: block; width: 22px; height: 2px;
          background: var(--warm-text); border-radius: 2px;
          transition: 0.2s;
        }

        .mobile-nav {
          display: none;
          position: fixed; inset: 0; z-index: 200;
          background: var(--cream);
          flex-direction: column; align-items: center; justify-content: center;
          gap: 32px;
        }
        .mobile-nav.open { display: flex; }
        .mobile-nav a {
          font-family: 'Playfair Display', serif;
          font-size: 1.8rem; font-weight: 500;
          color: var(--warm-text); text-decoration: none;
        }
        .mobile-nav-close {
          position: absolute; top: 20px; right: 24px;
          background: none; border: none; cursor: pointer;
          font-size: 2rem; color: var(--muted-text); line-height: 1;
        }

        .hero {
          min-height: calc(100vh - 68px);
          display: grid;
          grid-template-columns: 1.1fr 0.9fr;
          align-items: center;
          gap: 60px;
          padding: 80px 48px;
          position: relative;
          overflow: hidden;
          max-width: 1400px;
          margin: 0 auto;
        }

        .hero-content {
          text-align: left;
          position: relative;
          z-index: 2;
        }

        .hero-glow {
          position: absolute;
          top: 20%; left: 50%; transform: translate(-50%, -50%);
          width: 800px; height: 600px;
          background: radial-gradient(ellipse, var(--glow-amber) 0%, transparent 65%);
          pointer-events: none;
          animation: glow-pulse 5s ease-in-out infinite;
        }

        .hero-badge {
          display: inline-flex; align-items: center; gap: 7px;
          background: var(--sky-light);
          border: 1px solid var(--sky);
          color: var(--teal-deep);
          padding: 7px 16px;
          border-radius: 100px;
          font-size: 0.8rem; font-weight: 600;
          margin-bottom: 24px;
          animation: fade-up 0.6s ease both;
        }

        .hero-badge-dot {
          width: 6px; height: 6px; border-radius: 50%;
          background: var(--teal);
          animation: breathe 3s ease-in-out infinite;
        }

        .hero h1 {
          font-family: 'Playfair Display', serif;
          font-size: clamp(2.8rem, 5vw, 4.2rem);
          font-weight: 600;
          line-height: 1.1;
          letter-spacing: -1px;
          color: var(--warm-text);
          margin-bottom: 24px;
          animation: fade-up 0.7s ease 0.1s both;
        }

        .hero h1 em {
          font-style: italic;
          color: var(--teal);
        }

        .hero-sub {
          font-size: clamp(1.1rem, 2vw, 1.25rem);
          font-weight: 400;
          color: var(--muted-text);
          max-width: 540px;
          line-height: 1.6;
          margin-bottom: 40px;
          animation: fade-up 0.7s ease 0.2s both;
        }

        .vignette-card {
          background: white;
          border-radius: 24px;
          padding: 32px;
          box-shadow: 0 30px 90px rgba(42,122,138,0.18);
          position: relative;
          z-index: 2;
          border: 1px solid rgba(196,212,190,0.3);
          animation: fade-up 0.8s ease 0.4s both;
        }

        .vignette-label {
          font-size: 0.75rem; font-weight: 600; text-transform: uppercase;
          letter-spacing: 1px; color: var(--faint-text); margin-bottom: 20px;
          display: block;
        }

        .vignette-moment {
          font-family: 'Playfair Display', serif;
          font-size: 1.25rem; font-weight: 600;
          color: var(--warm-text); margin-bottom: 24px;
          line-height: 1.4;
        }

        .vignette-nudge {
          background: var(--cream-dark);
          border-radius: 16px;
          padding: 20px;
          border-left: 4px solid var(--teal);
          margin-bottom: 20px;
        }

        .vignette-outcome {
          font-size: 0.875rem; color: var(--teal-deep);
          font-weight: 500; display: flex; align-items: center; gap: 8px;
        }

        /* ── TRY FLOW ── */
        .try-flow-container {
          background: var(--white);
          border-radius: 20px;
          padding: 32px;
          border: 1.5px solid var(--sage);
          max-width: 480px;
          width: 100%;
          box-shadow: 0 12px 40px rgba(0,0,0,0.04);
          text-align: left;
        }

        .try-title {
          font-family: 'Playfair Display', serif;
          font-size: 1.4rem; font-weight: 600;
          margin-bottom: 8px;
        }

        .try-sub { font-size: 0.9rem; color: var(--muted-text); margin-bottom: 24px; }

        .try-form { display: flex; flex-direction: column; gap: 16px; }

        .try-input-group { display: flex; flex-direction: column; gap: 6px; }
        .try-input-group label { font-size: 0.8rem; font-weight: 600; color: var(--faint-text); }
        .try-input {
          height: 48px; border: 1.5px solid var(--sage); border-radius: 12px;
          padding: 0 16px; font-family: inherit; font-size: 0.9rem; outline: none;
        }
        .try-input:focus { border-color: var(--teal); box-shadow: 0 0 0 3px var(--teal-pale); }

        .try-select {
          height: 48px; border: 1.5px solid var(--sage); border-radius: 12px;
          padding: 0 12px; font-family: inherit; font-size: 0.9rem; outline: none;
          background: white; cursor: pointer;
        }

        .try-submit {
          height: 50px; background: var(--teal); color: white; border: none;
          border-radius: 100px; font-weight: 600; font-size: 0.95rem; cursor: pointer;
          transition: background 0.2s; margin-top: 8px;
        }
        .try-submit:hover { background: var(--teal-deep); }

        .demo-result { animation: fade-in 0.5s ease; }
        .demo-nudge-card {
          background: var(--sky-light); border: 1px solid var(--sky);
          border-radius: 16px; padding: 24px; margin-bottom: 20px;
        }
        .demo-nudge-body { font-size: 1rem; line-height: 1.6; color: var(--warm-text); margin-bottom: 12px; font-weight: 500; }
        .demo-nudge-science { font-size: 0.75rem; color: var(--teal-deep); font-weight: 600; text-transform: uppercase; letter-spacing: 0.5px; }

        .demo-actions { display: flex; flex-direction: column; gap: 10px; }
        .demo-btn-primary {
          background: var(--teal); color: white; border: none; padding: 12px;
          border-radius: 100px; font-weight: 600; cursor: pointer; text-align: center; text-decoration: none;
        }
        .demo-btn-secondary {
          background: transparent; color: var(--muted-text); border: 1px solid var(--sage);
          padding: 10px; border-radius: 100px; font-size: 0.85rem; cursor: pointer; text-align: center;
        }

        /* ── AUDIENCE SECTIONS ── */
        .audience-section { padding: 100px 48px; background: var(--white); }
        .audience-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 32px; max-width: 1200px; margin: 0 auto; }
        .audience-card {
          padding: 60px; border-radius: 32px; position: relative; overflow: hidden;
        }
        .audience-hr { background: var(--dark-teal); color: white; }
        .audience-mgr { background: var(--cream-dark); color: var(--warm-text); }

        .audience-label { font-size: 0.8rem; font-weight: 700; text-transform: uppercase; letter-spacing: 1.5px; margin-bottom: 24px; display: block; opacity: 0.8; }
        .audience-title { font-family: 'Playfair Display', serif; font-size: 2.2rem; font-weight: 600; margin-bottom: 24px; line-height: 1.2; }
        .audience-points { list-style: none; padding: 0; margin-bottom: 40px; }
        .audience-points li { margin-bottom: 20px; display: flex; gap: 12px; font-size: 1rem; line-height: 1.5; }
        .audience-hr .audience-points li::before { content: '✓'; color: var(--sky); font-weight: 900; }
        .audience-mgr .audience-points li::before { content: '→'; color: var(--teal); font-weight: 900; }
        
        .audience-cta {
          display: inline-block; padding: 14px 28px; border-radius: 100px; font-weight: 600; text-decoration: none; transition: transform 0.2s;
        }
        .audience-hr .audience-cta { background: var(--sky); color: var(--dark-teal); }
        .audience-mgr .audience-cta { background: var(--teal); color: white; }
        .audience-cta:hover { transform: translateY(-2px); }

        /* ── REST OF STYLES ── */
        .proof-strip { background: var(--cream); padding: 40px 24px; text-align: center; border-bottom: 1px solid var(--cream-dark); }
        .proof-strip-label { font-size: 0.75rem; font-weight: 600; text-transform: uppercase; letter-spacing: 1.5px; color: var(--faint-text); margin-bottom: 24px; }
        .proof-logos { display: flex; justify-content: center; gap: 60px; opacity: 0.6; grayscale: 1; }
        .proof-logo { font-size: 1.1rem; font-weight: 700; color: var(--warm-text); }

        .pricing-section {
          background: var(--cream);
          padding: 120px 24px;
          position: relative;
          z-index: 1;
        }

        /* Bridge between Dark Founder and Light Pricing */
        .founder-bridge {
          height: 100px;
          background: linear-gradient(to bottom, var(--dark-teal), var(--cream));
          margin-top: -1px;
        }

        @media (max-width: 1000px) {
          .hero { grid-template-columns: 1fr; padding: 60px 24px; text-align: center; }
          .hero-content { display: flex; flex-direction: column; align-items: center; }
          .hero-sub { margin-left: auto; margin-right: auto; }
          .audience-grid { grid-template-columns: 1fr; }
          .audience-card { padding: 40px 24px; }
        }

        @media (max-width: 600px) {
          .hero h1 { font-size: 2.4rem; }
          .proof-logos { flex-wrap: wrap; gap: 30px; }
        }

        .founder-video-container {
          margin: 48px auto 0;
          width: 100%;
          max-width: 860px;
          aspect-ratio: 16/9;
          border-radius: 20px;
          overflow: hidden;
          box-shadow: 0 24px 80px rgba(0,0,0,0.3);
          border: 1px solid rgba(255,255,255,0.1);
          background: #000;
        }
        .founder-video-container iframe {
          width: 100%; height: 100%;
          display: block; border: none;
        }

        .founder-section { background: var(--dark-teal); color: white; padding: 100px 24px; }
        .founder-inner { max-width: 900px; margin: 0 auto; }
        .founder-headline { font-family: 'Playfair Display', serif; font-size: clamp(1.8rem, 4vw, 2.8rem); font-weight: 600; line-height: 1.2; color: white; margin-bottom: 24px; }
        .founder-headline em { font-style: italic; color: var(--sky); }
        .founder-body p { font-size: 1.1rem; color: rgba(255,255,255,0.8); line-height: 1.7; margin-bottom: 20px; }

        .hiw-section { background: var(--cream); padding: 100px 24px; }
        .hiw-header { text-align: center; margin-bottom: 72px; }
        .steps-grid {
          display: grid; grid-template-columns: repeat(3, 1fr);
          gap: 2px;
          background: var(--sage);
          border-radius: 24px; overflow: hidden;
          max-width: 1100px; margin: 0 auto;
        }

        .step-card {
          background: var(--cream);
          padding: 40px 36px;
          position: relative;
        }

        .step-num {
          font-family: 'Playfair Display', serif;
          font-size: 3rem; font-weight: 600;
          color: var(--sky);
          line-height: 1;
          margin-bottom: 20px;
          display: block;
        }

        .step-icon { font-size: 1.8rem; margin-bottom: 14px; }
        .step-title {
          font-family: 'Playfair Display', serif;
          font-size: 1.15rem; font-weight: 600;
          color: var(--warm-text); margin-bottom: 10px;
        }
        .step-desc { font-size: 0.9rem; color: var(--muted-text); line-height: 1.65; }

        .pricing-header { text-align: center; margin-bottom: 80px; }
        .pricing-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 32px; max-width: 900px; margin: 0 auto; }
        .plan-card {
          background: white;
          border: 1px solid var(--sage);
          border-radius: 32px;
          padding: 48px;
          transition: all 0.3s ease;
          position: relative;
          display: flex;
          flex-direction: column;
        }
        .plan-card:hover { transform: translateY(-8px); box-shadow: 0 20px 60px rgba(42,122,138,0.1); }
        .plan-card.featured {
          border: 2px solid var(--teal);
          box-shadow: 0 30px 90px rgba(42,122,138,0.15);
        }
        .plan-card.featured::before {
          content: 'Free Forever';
          position: absolute;
          top: -14px; left: 50%;
          transform: translateX(-50%);
          background: var(--teal);
          color: white;
          padding: 6px 16px;
          border-radius: 100px;
          font-size: 0.75rem;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 1px;
        }
        .plan-badge { display: inline-block; color: var(--faint-text); font-size: 0.75rem; font-weight: 700; letter-spacing: 1px; text-transform: uppercase; margin-bottom: 12px; }
        .plan-name { font-family: 'Playfair Display', serif; font-size: 1.75rem; font-weight: 600; color: var(--warm-text); margin-bottom: 8px; }
        .plan-price { font-family: 'Playfair Display', serif; font-size: 3.5rem; font-weight: 600; color: var(--warm-text); line-height: 1; margin-bottom: 12px; }
        .plan-sub { font-size: 0.85rem; color: var(--muted-text); margin-bottom: 32px; }
        .plan-features { list-style: none; margin-bottom: 40px; padding: 0; flex-grow: 1; }
        .plan-features li { display: flex; align-items: flex-start; gap: 12px; font-size: 0.95rem; color: var(--muted-text); padding: 12px 0; border-bottom: 1px solid var(--cream-dark); }
        .plan-features li:last-child { border-bottom: none; }
        .plan-check { color: var(--teal); font-weight: 700; flex-shrink: 0; }
        .plan-cta { display: block; text-align: center; padding: 16px; border-radius: 100px; font-size: 1rem; font-weight: 600; text-decoration: none; transition: 0.2s; }
        .plan-cta.primary { background: var(--teal); color: white; }
        .plan-cta.primary:hover { background: var(--teal-deep); }
        .plan-cta.ghost { background: transparent; color: var(--muted-text); border: 2px solid var(--sage); }
        .plan-cta.ghost:hover { background: var(--cream); border-color: var(--teal); color: var(--teal); }

        .final-cta-section {
          background: white;
          text-align: center;
          padding: 140px 24px;
          border-top: 1px solid var(--sage);
        }
        .final-cta-inner { max-width: 600px; margin: 0 auto; }
        .final-cta-title { font-family: 'Playfair Display', serif; font-size: 3rem; font-weight: 600; color: var(--warm-text); margin-bottom: 24px; line-height: 1.1; }
        .final-cta-sub { font-size: 1.1rem; color: var(--muted-text); margin-bottom: 48px; line-height: 1.6; }

        footer { background: var(--cream); border-top: 1px solid var(--sage); padding: 36px 48px; }
        .footer-inner { max-width: 1100px; margin: 0 auto; display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 16px; }
        .footer-logo { font-family: 'Playfair Display', serif; font-size: 1.1rem; font-weight: 600; color: var(--teal-deep); text-decoration: none; display: flex; align-items: center; gap: 8px; }
        .footer-links { display: flex; gap: 28px; flex-wrap: wrap; }
        .footer-links a { font-size: 0.82rem; color: var(--muted-text); text-decoration: none; }
        .footer-made { font-size: 0.78rem; color: var(--faint-text); }

        .hero-form { display: flex; gap: 10px; flex-wrap: wrap; justify-content: center; }
        .hero-input { height: 50px; background: white; border: 1.5px solid var(--sage); border-radius: 100px; padding: 0 22px; font-family: inherit; }
        .hero-submit { height: 50px; background: var(--teal); color: white; border: none; border-radius: 100px; padding: 0 28px; font-weight: 600; cursor: pointer; }
        .hero-success { align-items: center; gap: 8px; background: var(--sage-light); border: 1px solid var(--sage); color: var(--teal-deep); padding: 12px 22px; border-radius: 100px; font-size: 0.9rem; }
      `}</style>

      {/* MOBILE NAV OVERLAY */}
      <div className={`mobile-nav ${isMobileNavOpen ? 'open' : ''}`}>
        <button type="button" className="mobile-nav-close" onClick={() => setIsMobileNavOpen(false)} aria-label="Close menu">×</button>
        <a href="#outcomes" onClick={() => setIsMobileNavOpen(false)}>outcomes</a>
        <a href="#how-it-works" onClick={() => setIsMobileNavOpen(false)}>how it works</a>
        <a href="#pricing" onClick={() => setIsMobileNavOpen(false)}>pricing</a>
        <Link to="/login" onClick={() => setIsMobileNavOpen(false)}>sign in</Link>
        <Link to="/login" className="nav-cta" onClick={() => setIsMobileNavOpen(false)}>start free</Link>
      </div>

      {/* NAV */}
      <nav>
        <Link to="/" className="nav-logo">
          <span className="nav-logo-mark">🕊</span>
          duva
        </Link>
        <div className="nav-links">
          <a href="#outcomes">outcomes</a>
          <a href="#how-it-works">how it works</a>
          <a href="#pricing">pricing</a>
        </div>
        <div className="nav-actions">
          <Link to="/login" className="nav-signin">sign in</Link>
          <Link to="/login" className="nav-cta">start free</Link>
          <button type="button" className="nav-burger" onClick={() => setIsMobileNavOpen(true)} aria-label="Open menu">
            <span></span><span></span><span></span>
          </button>
        </div>
      </nav>

      <main id="main-content">
        {/* HERO */}
        <section className="hero">
          <div className="hero-content">
            <div className="hero-badge">
              <span className="hero-badge-dot"></span>
              the outcome-first platform for people leaders
            </div>
            <h1>
              The quiet habit that builds<br />
              <em>high-standard managers</em>. Right in Slack.
            </h1>
            <p className="hero-sub">
              Stop "training and hoping." Duva delivers the right prompt at the moment of need, helping managers build the high-standard habits that drive retention and scale.
            </p>
            
            <div className="try-flow-container">
              {tryStep === 'input' && (
                <div className="try-step-input">
                  <h2 className="try-title">Experience the product</h2>
                  <p className="try-sub">No account needed. See the nudge you'd get this week.</p>
                  <form className="try-form" onSubmit={handleStartDemo}>
                    <div className="try-input-group">
                      <label htmlFor="user-name">Your first name</label>
                      <input 
                        id="user-name"
                        type="text" 
                        className="try-input" 
                        placeholder="e.g. Priya" 
                        value={userName}
                        onChange={e => setUserName(e.target.value)}
                        required
                      />
                    </div>
                    <div className="try-input-group">
                      <label htmlFor="user-focus">One thing you're working on this week</label>
                      <select 
                        id="user-focus"
                        className="try-select"
                        value={userFocus}
                        onChange={e => setUserFocus(e.target.value)}
                      >
                        {FOCUS_OPTIONS.map(opt => (
                          <option key={opt.id} value={opt.id}>{opt.label}</option>
                        ))}
                      </select>
                    </div>
                    <button type="submit" className="try-submit">Show me my nudge</button>
                  </form>
                </div>
              )}

              {tryStep === 'nudge' && (
                <div className="demo-result">
                  <h2 className="try-title">Here is your nudge, {userName || 'Manager'}</h2>
                  <p className="try-sub">This is exactly what would hit your Slack on Tuesday morning.</p>
                  <div className="demo-nudge-card">
                    <div className="nudge-bot-row">
                      <div className="nudge-bot-avatar">🕊</div>
                      <span className="nudge-bot-name">Duva</span>
                      <span className="nudge-bot-time">9:00 AM</span>
                    </div>
                    <p className="demo-nudge-body">{demoNudge?.body}</p>
                    <p className="demo-nudge-science">Evidence: {demoNudge?.science}</p>
                  </div>
                  <div className="demo-actions">
                    <Link to="/login" className="demo-btn-primary">Build this habit for my team</Link>
                    
                    <div style={{marginTop: 20, paddingTop: 20, borderTop: '1px solid var(--sky)'}}>
                      <p style={{fontSize: '0.85rem', marginBottom: 12, fontWeight: 600}}>Want to see how this lands? We'll send it to you.</p>
                      <form onSubmit={handleSendEmail} style={{display: 'flex', gap: 8}}>
                        <input 
                          type="email" 
                          className="try-input" 
                          style={{flex: 1, height: 40}} 
                          placeholder="your email" 
                          value={demoEmail}
                          onChange={e => setDemoEmail(e.target.value)}
                          required
                        />
                        <button type="submit" className="demo-btn-primary" style={{height: 40, padding: '0 16px', fontSize: '0.8rem'}} disabled={isSendingDemo}>
                          {isSendingDemo ? 'Sending...' : 'Send to me'}
                        </button>
                      </form>
                    </div>

                    <button onClick={() => setTryStep('input')} className="demo-btn-secondary" style={{marginTop: 10}}>Try another scenario</button>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="hero-visual">
            <div className="vignette-card">
              <span className="vignette-label">Scenario: Monday 9:00 AM</span>
              <p className="vignette-moment">
                Priya is about to start her week. One of her top engineers feels "disconnected."
              </p>
              <div className="vignette-nudge">
                <div className="nudge-bot-row" style={{marginBottom: 8}}>
                  <div className="nudge-bot-avatar" style={{width: 24, height: 24, fontSize: 12}}>🕊</div>
                  <span className="nudge-bot-name" style={{fontSize: 12}}>Duva</span>
                </div>
                <p style={{fontSize: '0.9rem', margin: 0}}>
                  "Before your 1:1 with Alex, try starting with: 'What’s one thing that happened last week that made you feel proud?'"
                </p>
              </div>
              <div className="vignette-outcome">
                <span style={{fontSize: 18}}>✨</span>
                <span>Alex opens up. A retention risk is caught early.</span>
              </div>
            </div>
          </div>
        </section>

        {/* SOCIAL PROOF STRIP */}
        <div className="proof-strip">
          <p className="proof-strip-label">trusted by people leaders at</p>
          <div className="proof-logos">
            <span className="proof-logo">InMobi</span>
            <span className="proof-logo">Dream11</span>
            <span className="proof-logo">Razorpay</span>
            <span className="proof-logo">Zomato</span>
            <span className="proof-logo">Freshworks</span>
          </div>
        </div>

        {/* DUAL AUDIENCE SECTION */}
        <section className="audience-section" id="outcomes">
          <div className="audience-grid">
            <div className="audience-card audience-hr">
              <span className="audience-label">For HR & People Leaders</span>
              <h2 className="audience-title">Scale manager quality without the overhead.</h2>
              <ul className="audience-points">
                <li>Stop "training and hoping" — nudges land at the moment behavior actually changes.</li>
                <li>Move the engagement needle every week, not just once a year.</li>
                <li>Get a "dashboard of habits" showing what your managers are actually doing.</li>
              </ul>
              <Link to="/login" className="audience-cta">See enterprise outcomes</Link>
            </div>
            <div className="audience-card audience-mgr">
              <span className="audience-label">For Managers</span>
              <h2 className="audience-title">Be more human, with less effort.</h2>
              <ul className="audience-points">
                <li>Leadership on autopilot — we do the thinking, you do the leading.</li>
                <li>Your personal Chief of Staff whispering the right thing to say.</li>
                <li>Small 60-second actions that prevent big Tuesday-morning fires.</li>
              </ul>
              <Link to="/login" className="audience-cta">Start free for your team</Link>
            </div>
          </div>
        </section>

        {/* HOW IT WORKS */}
        <section className="hiw-section" id="how-it-works">
          <div className="section-inner">
            <div className="hiw-header">
              <p className="section-eyebrow">how it works</p>
              <h2 className="section-title">three quiet steps</h2>
              <p className="section-sub" style={{margin: '0 auto'}}>Setup takes under 3 minutes. No dashboards to log into. Just a calm rhythm that becomes part of the week.</p>
            </div>
            <div className="steps-grid">
              <div className="step-card">
                <span className="step-num">01</span>
                <div className="step-icon">🔍</div>
                <h3 className="step-title">notice the moment</h3>
                <p className="step-desc">Duva delivers nudges at contextually optimal moments — before 1:1s, at the start of a standup, or during end-of-day wind-downs.</p>
              </div>
              <div className="step-card">
                <span className="step-num">02</span>
                <div className="step-icon">🕊</div>
                <h3 className="step-title">a gentle prompt</h3>
                <p className="step-desc">A single, specific, completable action arrives in Slack — requiring 60 seconds or less. Not a notification to dismiss.</p>
              </div>
              <div className="step-card">
                <span className="step-num">03</span>
                <div className="step-icon">🌱</div>
                <h3 className="step-title">patterns form slowly</h3>
                <p className="step-desc">Over 60 days, small repeated actions compound into measurable team-wide shifts in retention and engagement.</p>
              </div>
            </div>
          </div>
        </section>

        {/* FOUNDER STORY (Woven into positioning) */}
        <section className="founder-section">
          <div className="founder-inner">
            <h2 className="founder-headline" style={{textAlign: 'center', color: 'white'}}>Built by a founder who watched<br /><em>$100k L&D programs fail</em>.</h2>
            <div className="founder-body" style={{maxWidth: 700, margin: '0 auto 48px', textAlign: 'center', color: 'rgba(255,255,255,0.8)'}}>
              <p>Kevin Freitas (ex-InMobi) built Duva after 10 years of seeing learning vanish the moment a workshop ends. Real growth doesn't happen in a training room—it happens in the messy, busy moments of a Tuesday morning.</p>
            </div>

            <div className="founder-video-container">
              <iframe
                src={`https://app.heygen.com/embeds/${founderVideoId}`}
                title="A message from our founder"
                allow="fullscreen"
                allowFullScreen
                loading="lazy"
              />
            </div>
          </div>
        </section>

        <div className="founder-bridge" />

        {/* PRICING */}
        <section className="pricing-section" id="pricing">
          <div className="section-inner">
            <div className="pricing-header">
              <p className="section-eyebrow">simple pricing</p>
              <h2 className="section-title">start for free</h2>
              <p className="section-sub" style={{margin: '0 auto'}}>Duva is free forever for teams up to 50. No credit card, no catch.</p>
            </div>
            <div className="pricing-grid">
              <div className="plan-card featured">
                <span className="plan-badge">Standard</span>
                <h3 className="plan-name">For small teams</h3>
                <div className="plan-price">₹0</div>
                <p className="plan-sub">Free forever for up to 50 members</p>
                <ul className="plan-features">
                  <li><span className="plan-check">✓</span> Slack & Teams delivery</li>
                  <li><span className="plan-check">✓</span> Full access to nudge library</li>
                  <li><span className="plan-check">✓</span> Accountability buddy pairs</li>
                  <li><span className="plan-check">✓</span> Personal growth tracking</li>
                </ul>
                <Link to="/login" className="plan-cta primary">Start free for your team</Link>
              </div>
              <div className="plan-card">
                <span className="plan-badge">Enterprise</span>
                <h3 className="plan-name">For large orgs</h3>
                <div className="plan-price">Contact</div>
                <p className="plan-sub">Custom solutions for scale</p>
                <ul className="plan-features">
                  <li><span className="plan-check">✓</span> Unlimited team members</li>
                  <li><span className="plan-check">✓</span> Custom nudges & SSO</li>
                  <li><span className="plan-check">✓</span> Advanced engagement ROI</li>
                  <li><span className="plan-check">✓</span> Dedicated success manager</li>
                </ul>
                <a href="#enterprise-waitlist" className="plan-cta ghost">Request Pricing</a>
              </div>
            </div>
          </div>
        </section>

        {/* FINAL CTA SECTION */}
        <section className="final-cta-section" id="enterprise-waitlist">
          <div className="final-cta-inner">
            <h2 className="final-cta-title">Build a culture of<br /><em>quiet high performance.</em></h2>
            <p className="final-cta-sub">
              Join 1,200+ managers using science-backed nudges to lead with more confidence and less fatigue.
            </p>

            {finalStep === 'email' && (
              <form className="hero-form" onSubmit={handleFinalEmail}>
                <input
                  type="email"
                  className="hero-input"
                  placeholder="your work email"
                  value={finalEmail}
                  onChange={e => setFinalEmail(e.target.value)}
                  required
                />
                <button type="submit" className="hero-submit">Join waitlist</button>
              </form>
            )}

            {finalStep === 'challenge' && (
              <div className="challenge-step" style={{margin: '0 auto'}}>
                <p className="challenge-question">What's your #1 team challenge?</p>
                <div className="challenge-options">
                  {['burnout', 'recognition', 'leadership', 'inclusion'].map(c => (
                    <button
                      key={c}
                      className="challenge-btn"
                      onClick={() => void handleFinalChallenge(c)}
                      type="button"
                    >
                      {c.charAt(0).toUpperCase() + c.slice(1)}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {finalStep === 'success' && (
              <div className="hero-success" style={{display:'flex'}}>🕊 we'll be in touch soon.</div>
            )}
          </div>
        </section>
      </main>

      <footer>
        <div className="footer-inner">
          <Link to="/" className="footer-logo">🕊 duva</Link>
          <div className="footer-links">
            <Link to="/dashboard/library">Nudge Library</Link>
            <Link to="/login">Sign in</Link>
            <Link to="/privacy">Privacy</Link>
            <Link to="/terms">Terms</Link>
            <Link to="/security">Security</Link>
          </div>
          <p className="footer-made">Built for how teams actually work</p>
        </div>
      </footer>
    </div>
  )
}
